import React, { useState, useMemo } from 'react';
import {
  FileText,
  Plus,
  Star,
  Edit3,
  Trash2,
  Clock,
  BookOpen,
  ChevronRight,
  Search,
  Filter
} from 'lucide-react';
import { toast } from 'react-hot-toast';

/**
 * Draft Library - Central hub for all essay drafts
 *
 * Shows essays from:
 * - Workspace
 * - Outline development
 * - Direct creation
 *
 * Actions:
 * - Create new draft
 * - Edit existing draft
 * - Delete draft
 * - Assign to college slot
 */
export default function DraftLibrary({
  essays = [],
  onSelectEssay,
  onCreateNew,
  onDeleteEssay,
  colleges = []
}) {
  const [filter, setFilter] = useState('all'); // 'all' | 'personal' | 'supplemental' | 'scholarship' | 'honors'
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent'); // 'recent' | 'alphabetical' | 'word-count'

  // Get active (non-deleted) drafts
  const activeDrafts = useMemo(() => {
    return essays.filter(essay =>
      !essay.deleted &&
      !essay.is_idea &&
      !essay.is_freewrite
    );
  }, [essays]);

  // Filter and search drafts
  const filteredDrafts = useMemo(() => {
    let filtered = activeDrafts;

    // Apply type filter
    if (filter !== 'all') {
      filtered = filtered.filter(essay => essay.type === filter);
    }

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(essay =>
        essay.title?.toLowerCase().includes(query) ||
        essay.content?.toLowerCase().includes(query) ||
        essay.notes?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'recent') {
        return new Date(b.updated_at || b.last_updated) - new Date(a.updated_at || a.last_updated);
      }
      if (sortBy === 'alphabetical') {
        return (a.title || 'Untitled').localeCompare(b.title || 'Untitled');
      }
      if (sortBy === 'word-count') {
        return (b.word_count || 0) - (a.word_count || 0);
      }
      return 0;
    });

    return filtered;
  }, [activeDrafts, filter, searchQuery, sortBy]);

  // Group by status
  const groupedDrafts = useMemo(() => {
    return {
      active: filteredDrafts.filter(d => d.status !== 'completed' && d.status !== 'archived'),
      completed: filteredDrafts.filter(d => d.status === 'completed'),
      archived: filteredDrafts.filter(d => d.status === 'archived')
    };
  }, [filteredDrafts]);

  // Get type counts
  const typeCounts = useMemo(() => {
    return {
      all: activeDrafts.length,
      'personal': activeDrafts.filter(e => e.type === 'personal').length,
      supplemental: activeDrafts.filter(e => e.type === 'supplemental').length,
      scholarship: activeDrafts.filter(e => e.type === 'scholarship').length,
      honors: activeDrafts.filter(e => e.type === 'honors').length
    };
  }, [activeDrafts]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 bg-slate-900 rounded-lg p-6">
        <div>
          <h2 className="text-2xl font-bold text-stone-50">Essay Drafts</h2>
          <p className="text-stone-50 mt-1">
            Your essay library - manage all your college essays in one place
          </p>
        </div>
        <button
          onClick={() => onCreateNew({ type: 'supplemental', title: 'Untitled Draft', status: 'draft' })}
          className="px-4 py-2 bg-teal-500 border border-teal-50 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium flex items-center gap-2 whitespace-nowrap"
        >
          <Plus className="w-4 h-4" />
          New Draft
        </button>
      </div>
      

      {/* Search and Filters */}
      <div className="space-y-3">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search drafts by title, content, or notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border-2 border-slate-300 rounded-lg focus:border-teal-500 focus:ring-0 transition-colors"
          />
        </div>

        {/* Type Filters */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-slate-900 text-white'
                : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
            }`}
          >
            All ({typeCounts.all})
          </button>
          <button
            onClick={() => setFilter('personal')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'personal'
                ? 'bg-slate-900 text-white'
                : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
            }`}
          >
            Personal Statement ({typeCounts['personal']})
          </button>
          <button
            onClick={() => setFilter('supplemental')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'supplemental'
                ? 'bg-slate-900 text-white'
                : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
            }`}
          >
            Supplemental ({typeCounts.supplemental})
          </button>
          <button
            onClick={() => setFilter('scholarship')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'scholarship'
                ? 'bg-slate-900 text-white'
                : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
            }`}
          >
            Scholarship ({typeCounts.scholarship})
          </button>
          <button
            onClick={() => setFilter('honors')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'honors'
                ? 'bg-slate-900 text-white'
                : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
            }`}
          >
            Honors ({typeCounts.honors})
          </button>
        </div>

        {/* Sort Options */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-600" />
          <span className="text-sm text-slate-600 font-medium">Sort by:</span>
          <button
            onClick={() => setSortBy('recent')}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              sortBy === 'recent'
                ? 'bg-slate-900 text-white'
                : 'text-slate-600 hover:bg-stone-100'
            }`}
          >
            Most Recent
          </button>
          <button
            onClick={() => setSortBy('alphabetical')}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              sortBy === 'alphabetical'
                ? 'bg-slate-900 text-white'
                : 'text-slate-600 hover:bg-stone-100'
            }`}
          >
            A-Z
          </button>
          <button
            onClick={() => setSortBy('word-count')}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              sortBy === 'word-count'
                ? 'bg-slate-900 text-white'
                : 'text-slate-600 hover:bg-stone-100'
            }`}
          >
            Word Count
          </button>
        </div>
      </div>

      {/* Empty State */}
      {filteredDrafts.length === 0 && (
        <div className="text-center py-12 bg-stone-50 rounded-xl border-2 border-dashed border-stone-300">
          <FileText className="w-16 h-16 text-stone-400 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-slate-900 mb-2">
            {searchQuery ? 'No matching drafts' : 'No drafts yet'}
          </h3>
          <p className="text-slate-600 mb-4">
            {searchQuery
              ? 'Try adjusting your search or filter criteria'
              : 'Create your first draft or develop an outline into a full essay'
            }
          </p>
          {!searchQuery && (
            <button
              onClick={() => onCreateNew({ type: 'supplemental', title: 'Untitled Draft', status: 'draft' })}
              className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium"
            >
              Create Draft
            </button>
          )}
        </div>
      )}

      {/* Active Drafts */}
      {groupedDrafts.active.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-teal-600 uppercase tracking-wide mb-3">
            Active Drafts ({groupedDrafts.active.length})
          </h3>
          <div className="flex flex-col gap-4">
            {groupedDrafts.active.map(essay => (
              <DraftCard
                key={essay.id}
                essay={essay}
                colleges={colleges}
                onSelect={() => onSelectEssay(essay)}
                onDelete={() => onDeleteEssay(essay.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Completed Drafts */}
      {groupedDrafts.completed.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-emerald-600 uppercase tracking-wide mb-3">
            Completed ({groupedDrafts.completed.length})
          </h3>
          <div className="flex flex-col gap-4">
            {groupedDrafts.completed.map(essay => (
              <DraftCard
                key={essay.id}
                essay={essay}
                colleges={colleges}
                onSelect={() => onSelectEssay(essay)}
                onDelete={() => onDeleteEssay(essay.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Archived Drafts */}
      {groupedDrafts.archived.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wide mb-3">
            Archived ({groupedDrafts.archived.length})
          </h3>
          <div className="flex flex-col gap-4">
            {groupedDrafts.archived.map(essay => (
              <DraftCard
                key={essay.id}
                essay={essay}
                colleges={colleges}
                onSelect={() => onSelectEssay(essay)}
                onDelete={() => onDeleteEssay(essay.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Individual Draft Card
 */
function DraftCard({ essay, colleges, onSelect, onDelete }) {
  const [showActions, setShowActions] = useState(false);

  // Get essay type details
  const getTypeDetails = () => {
    switch (essay.type) {
      case 'personal':
        return { color: 'bg-azure-100 text-azure-700 border-azure-200', label: 'Personal Statement' };
      case 'supplemental':
        return { color: 'bg-teal-100 text-teal-700 border-teal-200', label: 'Supplemental' };
      case 'scholarship':
        return { color: 'bg-gold-100 text-gold-700 border-gold-200', label: 'Scholarship' };
      case 'honors':
        return { color: 'bg-purple-100 text-purple-700 border-purple-200', label: 'Honors' };
      default:
        return { color: 'bg-stone-100 text-slate-700 border-stone-200', label: 'Draft' };
    }
  };

  // Get status badge
  const getStatusBadge = () => {
    if (essay.status === 'completed') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
          <BookOpen className="w-3 h-3" />
          Complete
        </span>
      );
    }
    return null;
  };

  // Get college name if assigned
  const getCollegeName = () => {
    if (!essay.college_id) return null;
    const college = colleges.find(c => c.id === essay.college_id);
    return college?.global_colleges?.name || college?.name;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 48) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get content preview
  const getPreview = () => {
    if (!essay.content) return 'No content yet';
    const text = essay.content.replace(/[#*\[\]]/g, '').trim();
    return text.length > 120 ? text.substring(0, 120) + '...' : text;
  };

  const typeDetails = getTypeDetails();
  const collegeName = getCollegeName();

  return (
    <div
      className="bg-white border-2 border-slate-300 rounded-xl p-4 hover:border-slate-900 hover:shadow-lg transition-all duration-200 cursor-pointer group relative"
      onClick={onSelect}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-slate-900 mb-1 truncate group-hover:text-teal-600 transition-colors">
            {essay.title || 'Untitled Essay'}
          </h3>
          {collegeName && (
            <p className="text-xs text-slate-600 flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-azure-500 rounded-full"></span>
              {collegeName}
            </p>
          )}
        </div>
        <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-900 transition-colors flex-shrink-0 ml-2" />
      </div>

      {/* Type and Status Badges */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className={`px-2 py-1 rounded text-xs font-medium border ${typeDetails.color}`}>
          {typeDetails.label}
        </span>
        {getStatusBadge()}
      </div>

      {/* Preview */}
      <p className="text-sm text-slate-600 leading-relaxed mb-3 line-clamp-3">
        {getPreview()}
      </p>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-stone-200">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <FileText className="w-3.5 h-3.5" />
            {essay.word_count || 0} words
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {formatDate(essay.updated_at || essay.last_updated)}
          </span>
        </div>

        {/* Quick Actions */}
        {showActions && (
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect();
              }}
              className="p-1.5 hover:bg-teal-50 rounded transition-colors"
              title="Edit draft"
            >
              <Edit3 className="w-4 h-4 text-teal-600" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (confirm('Delete this draft? This cannot be undone.')) {
                  onDelete();
                }
              }}
              className="p-1.5 hover:bg-red-50 rounded transition-colors"
              title="Delete draft"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
